import { Toast } from 'antd-mobile'
import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { BASE_URL, DEFAULT_HEADERS, REQUEST_TIMEOUT, USE_MOCK } from './config'
import { mockHandlers } from './mock'

// 请求响应接口
export interface ApiResponse<T = unknown> {
  code: number
  data: T
  message?: string
  msg?: string
  err?: {
    code: string
    msg: string
  }
}

// 扩展配置
export interface RequestConfig extends AxiosRequestConfig {
  /** 跳过401自动跳转 */
  skipAuthRedirect?: boolean
  /** 跳过401自动弹窗 */
  skipAuthToast?: boolean
}

// Mock 标记头
const MOCK_HEADER = 'X-Mock'

// 创建 axios 实例
const request = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: DEFAULT_HEADERS,
  withCredentials: true,
})

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加 token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`)
    }

    // Mock 标记
    if (USE_MOCK && config.url && config.url in mockHandlers) {
      config.headers.set(MOCK_HEADER, 'true')
    }

    return config
  },
  (error: AxiosError) => Promise.reject(error),
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, config } = response

    // Mock 响应处理
    if (config.headers.get(MOCK_HEADER) === 'true' && config.url) {
      const mockHandler = mockHandlers[config.url as keyof typeof mockHandlers]
      if (mockHandler) {
        return mockHandler(config)
      }
    }

    // 兼容两种响应格式：{ code: 0, data: {...} } 和 { code: 200, msg: 'success', data: {...} }
    const isSuccess = data.code === 0 || data.code === 200
    const message = data.message || data.msg || '请求失败'

    if (isSuccess) {
      return data.data
    }

    // 业务错误提示
    const errorMessage = data.code === 1 && data.err?.msg ? data.err.msg : message

    Toast.show({
      content: errorMessage,
      icon: 'fail',
    })

    return Promise.reject(new Error(errorMessage))
  },
  (error: AxiosError) => {
    // Mock 错误处理
    if (USE_MOCK && error.config?.url) {
      const mockHandler = mockHandlers[error.config.url as keyof typeof mockHandlers]
      if (mockHandler) {
        return mockHandler(error.config)
      }
    }

    // 网络错误处理
    let message = '网络错误，请稍后重试'
    let showToast = true

    if (error.response) {
      const requestConfig = error.config as RequestConfig | undefined

      switch (error.response.status) {
        case 400:
          message = '请求参数错误'
          break
        case 401:
          showToast = false
          localStorage.removeItem('token')

          const isLoginPage = window.location.pathname === '/login'
          const skipRedirect = requestConfig?.skipAuthRedirect

          if (!isLoginPage && !skipRedirect) {
            // window.location.href = '/login'
          }
          break
        case 403:
          message = '拒绝访问'
          break
        case 404:
          message = '请求资源不存在'
          break
        case 500:
          message = '服务器错误'
          break
        case 502:
          message = '网关错误'
          break
        case 503:
          message = '服务不可用'
          break
        case 504:
          message = '网关超时'
          break
        default:
          message = `请求错误 ${error.response.status}`
      }
    } else if (error.code === 'ECONNABORTED') {
      message = '请求超时，请稍后重试'
    }

    if (showToast) {
      Toast.show({
        content: message,
        icon: 'fail',
      })
    }

    return Promise.reject(error)
  },
)

// 封装常用请求方法
export const http = {
  get: <T = unknown>(url: string, config?: RequestConfig): Promise<T> =>
    request.get(url, config),

  post: <T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> =>
    request.post(url, data, config),

  put: <T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> =>
    request.put(url, data, config),

  delete: <T = unknown>(url: string, config?: RequestConfig): Promise<T> =>
    request.delete(url, config),
}

export default request
